
import { useState, useRef, useCallback, useEffect } from 'react';
import { formatTime } from '../utils/timeFormat';
import { localizeTimeForSpeech } from '../utils/numberLocalization';
import type { SupportedLanguage } from '../utils/numberLocalization';

interface UseStopwatchProps {
  t?: (key: string, params?: any) => string;
  language?: string;
  soundEnabled?: boolean;
}

export function useStopwatch({ t, language, soundEnabled }: UseStopwatchProps = {}) {
  const [time, setTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const voiceAnnouncementRef = useRef<NodeJS.Timeout | null>(null);
  const lastAnnouncementTimeRef = useRef(0);
  const soundEnabledRef = useRef(soundEnabled);
  
  // 更新soundEnabled的ref值
  soundEnabledRef.current = soundEnabled;

  // 当soundEnabled变为false时，立即取消当前的语音播报
  useEffect(() => {
    if (!soundEnabled && 'speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
  }, [soundEnabled]);

  const announceTime = useCallback((currentTime: number) => {
    if ('speechSynthesis' in window && t && soundEnabled) {
      // 取消之前的语音
      speechSynthesis.cancel();
      
      // 格式化时间为更自然的语音
      const totalSeconds = Math.floor(currentTime / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      
      // 本地化数字用于语音播报
      const localizedTime = localizeTimeForSpeech(
        minutes, 
        seconds, 
        (language || 'en') as SupportedLanguage
      );
      
      let timeText = '';
      if (minutes > 0) {
        if (seconds > 0) {
          timeText = t('common:message.stopwatchTimeElapsed', { 
            minutes: localizedTime.minutes, 
            seconds: localizedTime.seconds 
          });
        } else {
          timeText = t('common:message.stopwatchMinutesElapsed', { 
            minutes: localizedTime.minutes 
          });
        }
      } else {
        timeText = t('common:message.stopwatchSecondsElapsed', { 
          seconds: localizedTime.seconds 
        });
      }
      
      const utterance = new SpeechSynthesisUtterance(timeText);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 0.8;
      // 根据当前语言设置语音语言
      utterance.lang = language === 'zh' ? 'zh-CN' : 'en-US';
      
      console.log('秒表语音播报:', timeText, '原始数字:', {minutes, seconds}, '本地化数字:', localizedTime);
      speechSynthesis.speak(utterance);
    }
  }, [t, language, soundEnabled]);

  const start = useCallback(() => {
    if (!isRunning) {
      setIsRunning(true);
      lastAnnouncementTimeRef.current = Math.floor(time / 10000) * 10000; // 重置上次提示时间
      
      intervalRef.current = setInterval(() => {
        setTime((prevTime) => {
          const newTime = prevTime + 10;
          
          // 检查是否需要语音提示（每10秒）
          const currentTenSeconds = Math.floor(newTime / 10000) * 10000;
          if (currentTenSeconds > lastAnnouncementTimeRef.current && currentTenSeconds >= 10000) {
            lastAnnouncementTimeRef.current = currentTenSeconds;
            // 延迟一点点确保时间更新完成，并且使用最新的announceTime引用
            setTimeout(() => {
              // 使用ref来获取最新的soundEnabled状态
              if ('speechSynthesis' in window && t && soundEnabledRef.current) {
                announceTime(currentTenSeconds);
              }
            }, 50);
          }
          
          return newTime;
        });
      }, 10);
    }
  }, [isRunning, time, announceTime, t]);

  const pause = useCallback(() => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (voiceAnnouncementRef.current) {
      clearTimeout(voiceAnnouncementRef.current);
      voiceAnnouncementRef.current = null;
    }
    // 取消当前播放的语音
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
  }, []);

  const reset = useCallback(() => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (voiceAnnouncementRef.current) {
      clearTimeout(voiceAnnouncementRef.current);
      voiceAnnouncementRef.current = null;
    }
    setTime(0);
    lastAnnouncementTimeRef.current = 0;
    
    // 取消所有语音
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
  }, []);

  return {
    time,
    isRunning,
    start,
    pause,
    reset
  };
}
